import axios from "axios";

const API_URL = "https://ims.holdthechild.net"; // Backend base URL

// Helper to get JWT token from localStorage
export const getToken = () => localStorage.getItem("token");

/**
 * Create a new task.
 * POST /api/tasks
 * @param {Object} taskData - Task details (title, description, assignedTo, etc.)
 * @returns Created task object
 */
export const createTask = async (taskData) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await axios.post(
      `${API_URL}/api/tasks`,
      taskData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    // Log more meaningful error info
    if (error.response) {
      console.error(
        "createTask error:",
        error.response.data?.message || error.response.data || error.message
      );
    } else {
      console.error("createTask error:", error.message);
    }
    // Re-throw a cleaner error object
    throw new Error(error.response?.data?.message || "Task creation failed");
  }
};

/**
 * Get all tasks with optional filters, pagination, and sorting.
 * GET /api/tasks
 * @param {Object} queryParams - Optional filters: status, assignedTo, priority, activity, page, limit, sortBy, sortOrder
 * @returns Paginated list of tasks with metadata
 */
export const getAllTasks = async (queryParams = {}) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks`, {
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error("getAllTasks error:", error);
    throw error;
  }
};

/**
 * Get a single task by its ID.
 * GET /api/tasks/:id
 * @param {string} taskId - Task ID
 * @returns Task object with populated fields
 */
export const getTaskById = async (taskId) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks/${taskId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("getTaskById error:", error);
    throw error;
  }
};

/**
 * Update a task by its ID.
 * PUT /api/tasks/:id
 * @param {string} taskId - Task ID
 * @param {Object} updatedTask - Fields to update
 * @returns Updated task object
 */
export const updateTask = async (taskId, updatedTask) => {
  try {
    const token = getToken();
    const response = await axios.put(`${API_URL}/api/tasks/${taskId}`, updatedTask, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("updateTask error:", error);
    throw error;
  }
};

/**
 * Delete a task by its ID.
 * DELETE /api/tasks/:id
 * @param {string} taskId - Task ID
 * @returns Success message
 */
export const deleteTask = async (taskId) => {
  try {
    const token = getToken();
    const response = await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("deleteTask error:", error);
    throw error;
  }
};

/**
 * Search tasks with full-text query and filters.
 * GET /api/tasks/search
 * @param {Object} queryParams - { q, status, assignedTo, priority, activity, page, limit }
 * @returns Paginated list of search results
 */
export const searchTasks = async (queryParams) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks/search`, {
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error("searchTasks error:", error);
    throw error;
  }
};

/**
 * Get tasks assigned to the current logged-in user.
 * GET /api/tasks/my
 * @param {Object} queryParams - Optional filters: status, priority, page, limit
 * @returns Paginated list of user's tasks
 */
export const getMyTasks = async (queryParams = {}) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks/my`, {
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error("getMyTasks error:", error);
    throw error;
  }
};

/**
 * Get tasks for a specific activity ID.
 * GET /api/tasks/activity/:activityId
 * @param {string} activityId - Activity ID
 * @param {Object} queryParams - Optional sorting params: sortBy, sortOrder
 * @returns List of tasks for the activity
 */
export const getTasksByActivityId = async (activityId, queryParams = {}) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks/activity/${activityId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error("getTasksByActivityId error:", error);
    throw error;
  }
};

/**
 * Add a comment to a task.
 * POST /api/tasks/:id/comments
 * @param {string} taskId - Task ID
 * @param {Object} commentData - { content: string }
 * @returns Newly added comment
 */
export const addComment = async (taskId, commentData) => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_URL}/api/tasks/${taskId}/comments`, commentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("addComment error:", error);
    throw error;
  }
};

/**
 * Get a pre-signed S3 upload URL for task attachments.
 * GET /api/tasks/attachments/upload-url
 * @param {string} fileName - Name of the file to upload
 * @param {string} fileType - MIME type of the file
 * @returns Upload URL and metadata
 */
export const getUploadUrl = async (fileName, fileType) => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/tasks/attachments/upload-url`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { fileName, fileType },
    });
    return response.data;
  } catch (error) {
    console.error("getUploadUrl error:", error);
    throw error;
  }
};

/**
 * Add an attachment to a task after uploading to S3.
 * POST /api/tasks/:taskId/attachments
 * @param {string} taskId - Task ID
 * @param {Object} attachmentData - { s3Key, fileName, size, mimeType }
 * @returns Newly added attachment details
 */
export const addAttachment = async (taskId, attachmentData) => {
  try {
    const token = getToken();
    const response = await axios.post(`${API_URL}/api/tasks/${taskId}/attachments`, attachmentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("addAttachment error:", error);
    throw error;
  }
};

/**
 * Delete an attachment from a task.
 * DELETE /api/tasks/:taskId/attachments/:attachmentId
 * @param {string} taskId - Task ID
 * @param {string} attachmentId - Attachment ID
 * @returns Success message
 */
export const deleteAttachment = async (taskId, attachmentId) => {
  try {
    const token = getToken();
    const response = await axios.delete(`${API_URL}/api/tasks/${taskId}/attachments/${attachmentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("deleteAttachment error:", error);
    throw error;
  }
};
